// src/pages/AboutUs.tsx

import React, { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import axios from "../api/axios";
import styles from "./AboutUs.module.css";
import type { AboutSection } from "../types/AboutSection";
import { useAuth } from "../context/AuthContext";

const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';

const getFullImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http')) return imageUrl;
  return `${process.env.REACT_APP_API_URL}${imageUrl}`;
};

const AboutUs: React.FC = () => {
  const { user, token } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [sections, setSections] = useState<AboutSection[]>([]);
  const [selectedSection, setSelectedSection] = useState<AboutSection | null>(null);
  const [tempTitle, setTempTitle] = useState<string>("");
  const [tempContent, setTempContent] = useState<string>("");
  const [tempBannerImage, setTempBannerImage] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    axios.get("/pages/about/sections")
      .then((res) => {
        // Sort sections by positionId before setting them to state
        const sortedSections = res.data.sort((a: AboutSection, b: AboutSection) => a.positionId - b.positionId);
        setSections(sortedSections);
      })
      .catch((err) => console.error("Failed to load about sections:", err));
  }, []);  

  const saveContent = async () => {
    if (!selectedSection) return;
    try {
      setIsSaving(true);
      await axios.put(`/pages/about/sections/${selectedSection.id}`, {
        title: tempTitle,
        content: tempContent,
        bannerImage: tempBannerImage || selectedSection.bannerImage,
      });
      setSections(prev => prev.map(sec =>
        sec.id === selectedSection.id
          ? { ...sec, title: tempTitle, content: tempContent, bannerImage: tempBannerImage || sec.bannerImage }
          : sec
      ));
      setSelectedSection(null);
    } catch (err) {
      console.error("Failed to save content:", err);
      alert("Error saving content.");
    } finally {
      setIsSaving(false);
    }
  };

  const createNewSection = async () => {
    if (sections.length >= 25) {
      alert("Maximum number of sections reached.");
      return;
    }
    try {
      const nextPositionId = sections.length > 0 ? Math.max(...sections.map(section => section.positionId)) + 1 : 1;  
      const res = await axios.post("/pages/about/sections", {
        title: "New Section",
        content: "<p>New section content.</p>",
        bannerImage: "",
        positionId: nextPositionId
      });
      const newSectionId = res.data.id;
      setSections(prev => [
        ...prev,
        { id: newSectionId, title: "New Section", content: "<p>New section content.</p>", bannerImage: "", positionId: nextPositionId }
      ]);
    } catch (err) {
      console.error("Failed to create new section:", err);
      alert("Error creating new section.");
    }
  };
  

  const deleteSection = async () => {
    if (!selectedSection) return;
    if (!window.confirm(`Are you sure you want to delete "${selectedSection.title}"? This cannot be undone.`)) return;
  
    try {
      await axios.delete(`/pages/about/sections/${selectedSection.id}`);
      setSections(prev => prev.filter(sec => sec.id !== selectedSection.id));
      setSelectedSection(null);
    } catch (err) {
      console.error("Failed to delete section:", err);
      alert("Error deleting section.");
    }
  };  

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1 className={styles.pageTitle}>About Us</h1>
        <p className={styles.pageDescription}>
          Learn more about our mission, vision, and organizational values.
        </p>

        {isAdmin && (
          <div style={{ marginBottom: "2rem", textAlign: "center" }}>
            {sections.length >= 15 ? (
              <p className={styles.maxSectionsError}>
                Maximum number of sections reached (15).
              </p>
            ) : (
              <button className="button outline" onClick={createNewSection}>
                ➕ Add New Section
              </button>
            )}
          </div>
        )}

        <div className={styles.cardGrid}>
          {sections.map((section) => (
            <div
              key={section.id}
              className={`${styles.card} ${isAdmin ? styles.cardClickable : ''}`}
              onClick={() => {
                if (isAdmin) {
                  setSelectedSection(section);
                  setTempTitle(section.title);
                  setTempContent(section.content);
                  setTempBannerImage(section.bannerImage || "");
                  setIsEditing(false);
                }
              }}
            >
              {section.bannerImage && (
                <div className={styles.cardBanner}>
                  <img src={getFullImageUrl(section.bannerImage) || ''} alt={section.title} className={styles.cardBannerImg} />
                </div>
              )}
              <div className={styles.cardTitle}>{section.title}</div>
              <div className={styles.cardContent}>
                <div dangerouslySetInnerHTML={{ __html: section.content }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedSection && (
        <div className={styles.modalOverlay} onClick={() => setSelectedSection(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalBody}>
              {tempBannerImage && (
                <img src={getFullImageUrl(tempBannerImage) || ''} alt={tempTitle} className={styles.modalBanner} />
              )}

              {isEditing ? (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Banner Image:</label>
                    <input
                      type="file"
                      accept="image/*"
                      className={styles.fileInput}
                      onChange={async (e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const file = e.target.files[0];
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pages/about/sections/upload/banner`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              },
                              body: formData,
                            });
                            if (!res.ok) {
                              throw new Error(`Upload failed with status: ${res.status}`);
                            }
                            const data = await res.json();
                            setTempBannerImage(data.imageUrl);
                          } catch (err) {
                            console.error('Banner upload failed:', err);
                            alert('Failed to upload banner image.');
                          }
                        }
                      }}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Title:</label>
                    <input
                      type="text"
                      value={tempTitle}
                      onChange={(e) => setTempTitle(e.target.value)}
                      className={styles.textInput}
                    />
                  </div>
                </>
              ) : (
                <h2 className={styles.modalTitle}>{tempTitle}</h2>
              )}

              {isEditing ? (
                <Editor
                  apiKey={tinymceApiKey}
                  value={tempContent}
                  onEditorChange={(newContent) => setTempContent(newContent)}
                  init={{
                    height: 400,
                    menubar: true,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'media', 'charmap', 'preview',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen', 'insertdatetime',
                      'table', 'help', 'wordcount', 'emoticons'
                    ],
                    toolbar:
                      'undo redo | formatselect | bold italic underline forecolor backcolor | ' +
                      'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
                      'link image media emoticons | removeformat | code fullscreen help',
                    automatic_uploads: true,
                    images_upload_url: `${process.env.REACT_APP_API_URL}/upload/image`,
                    file_picker_types: 'image',
                    file_picker_callback: (callback: any, value: any, meta: any) => {
                      if (meta.filetype === 'image') {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async () => {
                          const file = input.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pages/about/sections/upload/banner`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              },
                              body: formData,
                            });
                            if (!res.ok) {
                              throw new Error(`Upload failed with status: ${res.status}`);
                            }
                            const data = await res.json();
                            callback(data.imageUrl, { alt: file.name });
                          } catch (err) {
                            console.error('Upload failed:', err);
                            alert('Image upload failed.');
                          }
                        };
                        input.click();
                      }
                    }
                  }}
                />
              ) : (
                <div className={styles.modalContentDisplay} dangerouslySetInnerHTML={{ __html: selectedSection.content }} />
              )}
            </div>
            
            <div className={styles.modalButtons}>
              {isAdmin ? (
                isEditing ? (
                  <>
                    <button onClick={saveContent} disabled={isSaving} className="button outline">
                      {isSaving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={() => setIsEditing(false)} className="button">
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setIsEditing(true)} className="button outline">
                      Edit
                    </button>
                    <button
                      onClick={deleteSection}
                      className="button danger"
                    >
                      Delete
                    </button>
                  </>
                )
              ) : (
                <button onClick={() => setSelectedSection(null)} className="button">
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutUs;
